// ActBoundary.tsx — a processing screen shown once at an act boundary (end of
// ACT 1/2/3). Not a celebration and not an inline reflection: a deliberate pause
// that invites the user to let the act settle, calm the body, and return only when
// the act feels "done", not merely "completed". Carries the H4 guide moment at the
// top. Offers gentle routes to Meditations, Calm Your Body and the programme map.
import React from 'react';
import { Btn, Display, Body } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';
import { GuidePresence } from '@/flows/guide/GuidePresence';
import { useGuide } from '@/flows/guide/useGuide';

const ACT_NO: Record<string, number> = { act1: 1, act2: 2, act3: 3 };

export function ActBoundary({
  actId,
  onContinue,
  onTakeTime,
  onOpenMeditations,
  onOpenCalm,
  onOpenMap,
  onOpenTools,
}: {
  actId: 'act1' | 'act2' | 'act3';
  /** Ready to move into the next act — dismiss and carry on. */
  onContinue: () => void;
  /** Step away for a while — dismiss without moving on. */
  onTakeTime: () => void;
  onOpenMeditations: () => void;
  onOpenCalm: () => void;
  onOpenMap: () => void;
  /** Open the toolkit (My Stuff → Toolkit) to review the tools unlocked so far. */
  onOpenTools: () => void;
}) {
  const guide = useGuide();
  const n = ACT_NO[actId] ?? 1;
  const nextLabel = n < 3 ? `ACT ${n + 1}` : null;
  // Pick the H4 (act-matched) guide once; a local toggle lets it be tapped away.
  const [h4] = React.useState(() => guide.pick('H4_actBoundary', { act: actId }));
  const [showH4, setShowH4] = React.useState(true);
  const link = (icon: IconName, label: string, sub: string, onClick: () => void) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        textAlign: 'left',
        background: 'var(--paper)',
        border: '1px solid var(--hair)',
        borderRadius: 16,
        padding: '13px 15px',
        cursor: 'pointer',
        boxShadow: 'var(--sh1)',
      }}
    >
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: 999,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(29,158,117,0.12)',
          color: 'var(--green-deep)',
        }}
      >
        <Icon name={icon} size={19} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 15.5, color: 'var(--ink)' }}>
          {label}
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 1 }}>{sub}</span>
      </span>
      <span style={{ color: 'var(--ink-soft)', opacity: 0.4, flexShrink: 0 }}>
        <Icon name="chevRight" size={15} />
      </span>
    </button>
  );

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 88,
        background: 'var(--cream)',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        animation: 'rise .5s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      {/* soft green light from above — rest, not triumph */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 320,
          background:
            'radial-gradient(120% 90% at 50% -12%, rgba(150,210,184,0.5), rgba(29,158,117,0.12) 46%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', padding: 'calc(var(--safe-top) + 40px) 26px calc(28px + var(--safe-bottom))' }}>
        {/* H4 — the guide moment at the threshold (act-matched quote) */}
        {h4 && showH4 && (
          <div style={{ marginBottom: 22 }}>
            <GuidePresence selection={h4} onShown={() => guide.record(h4, actId)} onClose={() => setShowH4(false)} />
          </div>
        )}

        <div
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--green-deep)',
            marginBottom: 12,
          }}
        >
          ACT {n} complete
        </div>

        <Display parts={{ pre: 'A pause to let ', em: `ACT ${n}`, post: ' settle.' }} size={27} lh={1.18} />

        <Body size={14.5} style={{ marginTop: 16, maxWidth: 360, lineHeight: 1.62 }}>
          You've done deep work — this kind of material takes more out of us than it looks. There's no
          rush{nextLabel ? ` into ${nextLabel}` : ' onward'}. Many people find it helps to take a few
          days here, and let things process and digest.
        </Body>

        <Body size={14.5} style={{ marginTop: 13, maxWidth: 360, lineHeight: 1.62 }}>
          When you come back, notice gently: is anything still <em>unclear, confusing, or stirring mixed
          feelings</em>? If so, it's worth returning to those parts — nothing here is a race. Move on when
          this feels <strong>done</strong>, not just <strong>completed</strong>.
        </Body>

        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {link('leaf', 'Calm your body', 'Settle your system before you carry on', onOpenCalm)}
          {link('tool', 'Review your toolkit', 'Revisit the tools you’ve unlocked so far', onOpenTools)}
          {link('headphones', 'Meditations', 'A quiet practice in My Stuff', onOpenMeditations)}
          {link('compass', 'Your programme map', 'See how far you’ve come', onOpenMap)}
        </div>

        <div style={{ marginTop: 26, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Btn full size="lg" onClick={onContinue} leading={<Icon name="journey" size={18} />}>
            {nextLabel ? `I’m ready to begin ${nextLabel}` : 'Continue'}
          </Btn>
          <Btn full variant="ghost" onClick={onTakeTime}>
            I’ll take a few days first
          </Btn>
        </div>
      </div>
    </div>
  );
}
